/**
 * PATH: packages/email-hub/src/feature/pages/useComposeDragDrop.ts
 * FILE_ID: EMAILHUB-FEATURE-PAGES-COMPOSEDRAGDROP-001
 * PURPOSE: Page-wide drag-and-drop attachment support for the email
 *          composer. Tracks drag depth (enter/leave fire for every
 *          child node), exposes an `isDragActive` flag for the drop
 *          overlay, and hands dropped files to the caller.
 * WHY:     Operators could only drop IMAGES, and only directly onto
 *          the body editor — dropping a PDF anywhere on the compose
 *          surface did nothing ("can't drag and drop attachments
 *          here", 2026-06-10). This makes the whole page a drop
 *          target for any file type.
 * FLOW:
 *   dragenter/over (has Files) → preventDefault + overlay on
 *   drop → preventDefault → extractDroppedFiles → onFiles(files)
 * RELATED:
 *   - ComposePage.tsx: binds the returned props on its root div.
 *   - RichTextEditor.tsx: still embeds images dropped on the body
 *     inline; it stops propagation when it consumes a drop so the
 *     page handler doesn't double-attach.
 */
import { useCallback, useRef, useState } from 'react'

/** Does this drag carry OS files (vs text/in-page element drags)?
 *  Pure: DataTransfer-ish → boolean. */
export function dragHasFiles(dt: DataTransfer | null | undefined): boolean {
  if (!dt) return false
  const types = dt.types
  if (!types) return false
  // DOMStringList in some browsers, string[] in others/jsdom.
  return Array.from(types as ArrayLike<string>).includes('Files')
}

/** Pull the dropped File objects off a DataTransfer.
 *  Pure: DataTransfer-ish → File[] (empty when none). */
export function extractDroppedFiles(dt: DataTransfer | null | undefined): File[] {
  if (!dt) return []
  return Array.from(dt.files || [])
}

export interface ComposeDropzone {
  /** True while a file drag is hovering anywhere over the bound node —
   * drives the "Drop files to attach" overlay. */
  isDragActive: boolean
  /** Spread onto the compose page's root element. */
  dropzoneProps: {
    onDragEnter: (e: React.DragEvent<HTMLElement>) => void
    onDragOver: (e: React.DragEvent<HTMLElement>) => void
    onDragLeave: (e: React.DragEvent<HTMLElement>) => void
    onDrop: (e: React.DragEvent<HTMLElement>) => void
  }
}

export function useComposeDragDrop(
  onFiles: (files: File[]) => void,
): ComposeDropzone {
  const [isDragActive, setIsDragActive] = useState(false)
  // dragenter/dragleave fire for every child element crossed; a depth
  // counter is the standard way to know when the pointer truly left
  // the dropzone (depth back to 0) vs moved between children.
  const depthRef = useRef(0)

  const onDragEnter = useCallback((e: React.DragEvent<HTMLElement>) => {
    if (!dragHasFiles(e.dataTransfer)) return
    e.preventDefault()
    depthRef.current += 1
    setIsDragActive(true)
  }, [])

  const onDragOver = useCallback((e: React.DragEvent<HTMLElement>) => {
    if (!dragHasFiles(e.dataTransfer)) return
    // Required — without preventDefault the browser refuses the drop
    // and navigates to the file instead.
    e.preventDefault()
  }, [])

  const onDragLeave = useCallback((e: React.DragEvent<HTMLElement>) => {
    if (!dragHasFiles(e.dataTransfer)) return
    e.preventDefault()
    depthRef.current = Math.max(0, depthRef.current - 1)
    if (depthRef.current === 0) setIsDragActive(false)
  }, [])

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      if (!dragHasFiles(e.dataTransfer)) return
      // A child (the body editor) may have already consumed this drop —
      // it preventDefault()s and lets the event bubble so we can reset
      // the overlay here without double-attaching its files.
      const alreadyConsumed = e.defaultPrevented
      e.preventDefault()
      depthRef.current = 0
      setIsDragActive(false)
      if (alreadyConsumed) return
      const files = extractDroppedFiles(e.dataTransfer)
      if (files.length > 0) onFiles(files)
    },
    [onFiles],
  )

  return {
    isDragActive,
    dropzoneProps: { onDragEnter, onDragOver, onDragLeave, onDrop },
  }
}
